import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/JsonLdSchema';

const sections = [
  {
    id: 'section-1', num: '1', title: 'Definitions',
    content: 'For the purposes of these Terms and Conditions:',
    bullets: [
      '**User** refers to the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.',
      '**Service** refers to the DPOS website and any related software or applications provided by the Company.',
      '**Account** means a unique account created for You to access our Service or parts of our Service.',
      '**Website** refers to DPOS, accessible from dpos.co.in.',
    ]
  },
  {
    id: 'section-2', num: '2', title: 'Scope of Services',
    content: 'DPOS provides a comprehensive dynamic point of sale solution. We grant you a non-exclusive, non-transferable, revocable license to use the Service strictly in accordance with these Terms.',
    footer: 'We reserve the right to withdraw or amend our Service, and any service or material we provide on the Website, in our sole discretion without notice. We will not be liable if for any reason all or any part of the Website is unavailable at any time or for any period.'
  },
  {
    id: 'section-3', num: '3', title: 'User Accounts',
    content: 'When You create an account with Us, You must provide us information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of Your account on Our Service.',
    note: { title: 'Security Responsibility', text: 'You are responsible for safeguarding the password that You use to access the Service and for any activities or actions under Your password, whether Your password is with Our Service or a Third-Party Social Media Service.' }
  },
  {
    id: 'section-4', num: '4', title: 'Fees and Payments',
    content: 'Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis (such as daily, weekly, monthly or annually), depending on the type of Subscription plan you select when purchasing the Subscription.',
    footer: 'At the end of each period, Your Subscription will automatically renew under the exact same conditions unless You cancel it or DPOS cancels it. You may cancel Your Subscription renewal either through Your Account settings page or by contacting DPOS.'
  },
  {
    id: 'section-5', num: '5', title: 'Intellectual Property',
    content: 'The Service and its original content (excluding Content provided by You or other users), features and functionality are and will remain the exclusive property of DPOS and its licensors.',
    footer: 'The Service is protected by copyright, trademark, and other laws of both the Country and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of DPOS.'
  },
  {
    id: 'section-6', num: '6', title: 'Termination',
    content: 'We may terminate or suspend Your Account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.',
    footer: 'Upon termination, Your right to use the Service will cease immediately. If You wish to terminate Your Account, You may simply discontinue using the Service.'
  },
  {
    id: 'section-7', num: '7', title: 'Limitation of Liability',
    content: 'Notwithstanding any damages that You might incur, the entire liability of the Company and any of its suppliers under any provision of this Terms and Your exclusive remedy for all of the foregoing shall be limited to the amount actually paid by You through the Service or 100 USD if You haven\'t purchased anything through the Service.',
    footer: 'To the maximum extent permitted by applicable law, in no event shall the Company or its suppliers be liable for any special, incidental, indirect, or consequential damages whatsoever (including, but not limited to, damages for loss of profits, loss of data or other information, for business interruption, for personal injury, loss of privacy arising out of or in any way related to the use of or inability to use the Service, third-party software and/or third-party hardware used with the Service, or otherwise in connection with any provision of this Terms).'
  },
];

const toc = [
  { id: 'section-1', label: '1. Definitions' },
  { id: 'section-2', label: '2. Scope of Services' },
  { id: 'section-3', label: '3. User Accounts' },
  { id: 'section-4', label: '4. Fees and Payments' },
  { id: 'section-5', label: '5. Intellectual Property' },
  { id: 'section-6', label: '6. Termination' },
  { id: 'section-7', label: '7. Limitation of Liability' },
];

function renderText(str) {
  return str.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

export default function Terms() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Terms & Conditions – DPOS"
        description="Read the terms and conditions governing the use of the DPOS restaurant management and POS platform."
        canonical="/terms"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Terms & Conditions' },
      ]} />
      <section style={{ padding: '72px 24px 80px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '280px 1fr', gap: 64, alignItems: 'flex-start' }} className="legal-grid">

          {/* Sidebar */}
          <div style={{ position: 'sticky', top: 88 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Terms & Conditions</h1>
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 28 }}>Last Updated: October 24, 2025</p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Table of Contents</p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {toc.map(t => (
                <a key={t.id} href={`#${t.id}`} style={{ fontSize: 13, color: '#6b7280', padding: '6px 12px', borderRadius: 6, transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.background = '#fef2f2'; e.target.style.color = '#CC0000'; }}
                  onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
                >{t.label}</a>
              ))}
            </nav>
            <div style={{ marginTop: 32, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 6 }}>Need Help?</p>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>If you have any questions about these Terms, please contact us.</p>
              <Link to="/contact" style={{ fontSize: 13, fontWeight: 600, color: '#CC0000' }}>Contact Support →</Link>
            </div>
          </div>

          {/* Content */}
          <div>
            <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.8, marginBottom: 40, paddingBottom: 40, borderBottom: '1px solid #e5e7eb' }}>
              Please read these Terms and Conditions ("Terms", "Terms and Conditions") carefully before using the DPOS website and services (the "Service") operated by DPOS ("us", "we", or "our"). Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms.
            </p>

            {sections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: 56, paddingBottom: 56, borderBottom: '1px solid #f3f4f6' }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 16 }}>
                  {s.num}. {s.title}
                </h2>
                {s.content && <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.8, marginBottom: 16 }}>{s.content}</p>}
                {s.note && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>{s.note.title}</p>
                    <p style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>{s.note.text}</p>
                  </div>
                )}
                {s.bullets && (
                  <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {s.bullets.map((b, i) => (
                      <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, listStyle: 'disc' }}
                        dangerouslySetInnerHTML={{ __html: renderText(b) }} />
                    ))}
                  </ul>
                )}
                {s.footer && <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>{s.footer}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
      <style>{`@media(max-width:900px){.legal-grid{grid-template-columns:1fr !important;}}`}</style>
    </main>
  );
}
